import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Command as CommandIcon, BarChart3, Folder, Settings, Database, Sparkles, Layers, Bell } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useGlobalShortcut, useRegisteredShortcuts } from '@/hooks/useGlobalShortcut';
import { formatCombo } from '@/lib/globalShortcuts';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { visibleCategories as visibleAdminCategories } from '@/config/adminNav';
import { getTerm } from '@/lib/branding';

/**
 * Command palette (Cmd+K) — a single fuzzy-searchable list of every action you
 * can take in the platform. Sources:
 *   • Every shortcut registered via useGlobalShortcut() (Save, Undo, Redo, etc.)
 *   • Static navigation entries (Go to Dashboard, Go to Reports, etc.)
 *   • Role-aware entries (super_admin sees Tenant Admin / Recent Impersonations)
 *
 * Open with `Cmd+K` / `Ctrl+K`. Type to filter. Up/Down to move. Enter to run.
 * Esc to close.
 *
 * The palette also self-registers in the shortcut registry so it shows up in
 * the `?` help dialog, which keeps the discovery loop tight.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const shortcuts = useRegisteredShortcuts();

  useGlobalShortcut({
    id: 'global.command-palette',
    keys: 'mod+k',
    label: 'Open command palette',
    category: 'General',
    handler: () => setOpen(o => !o),
    allowInInputs: true,
  });

  // Reset query + selection on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Focus input next tick so Radix Dialog has mounted
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const navCommands = useMemo(() => buildNavCommands({ user, navigate, hasPermission }), [user, navigate, hasPermission]);
  const contentCommands = useContentCommands({ enabled: open, navigate });

  const allCommands = useMemo(() => {
    const fromShortcuts = shortcuts
      // Hide the palette itself + the help dialog from the list (they're meta)
      .filter(s => s.id !== 'global.command-palette' && s.id !== 'global.help')
      .map(s => ({
        id: s.id,
        label: s.label,
        category: s.category || 'Actions',
        keys: s.keys,
        run: () => s.handler({ preventDefault: () => {}, type: 'command-palette' }),
        icon: pickIconForCategory(s.category),
      }));
    return [...navCommands, ...contentCommands, ...fromShortcuts];
  }, [navCommands, contentCommands, shortcuts]);

  const filtered = useMemo(() => fuzzyFilter(allCommands, query), [allCommands, query]);

  // Group filtered results by category for visual structure
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(c => { (groups[c.category] = groups[c.category] || []).push(c); });
    return Object.entries(groups);
  }, [filtered]);

  // Flat index → command lookup for keyboard navigation
  const flatList = filtered;

  // Clamp active index when filter changes
  useEffect(() => {
    setActiveIndex(i => Math.max(0, Math.min(i, flatList.length - 1)));
  }, [flatList.length]);

  const runCommand = useCallback((cmd) => {
    if (!cmd) return;
    setOpen(false);
    // Defer the run a tick so the Dialog has fully unmounted before navigation/handler fires
    setTimeout(() => {
      try { cmd.run(); } catch (e) { console.error('command palette run failed:', e); }
    }, 0);
  }, []);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(flatList.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runCommand(flatList[activeIndex]);
    }
  }, [flatList, activeIndex, runCommand]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden gap-0" data-testid="command-palette">
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search and run any action or navigate anywhere on the platform.
        </DialogDescription>
        {/* Search input */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={onKeyDown}
            placeholder="Type a command or search..."
            className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground/80"
            data-testid="command-palette-input"
          />
          <kbd className="text-[9px] px-1.5 py-0.5 rounded bg-muted/40 border border-border/40 text-muted-foreground font-mono">esc</kbd>
        </div>

        {/* Command list */}
        <div className="max-h-[60vh] overflow-y-auto py-1" data-testid="command-palette-list">
          {flatList.length === 0 && (
            <p className="text-xs text-muted-foreground italic text-center py-8" data-testid="command-palette-empty">
              No matches for &quot;{query}&quot;
            </p>
          )}
          {grouped.map(([cat, cmds]) => (
            <div key={cat} data-testid={`command-palette-group-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground/80 font-semibold px-3 py-1.5 sticky top-0 bg-background/95 backdrop-blur">
                {cat}
              </p>
              {cmds.map(cmd => {
                const idx = flatList.indexOf(cmd);
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={cmd.id}
                    onClick={() => runCommand(cmd)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/20 text-foreground'}`}
                    data-testid={`command-palette-item-${cmd.id}`}
                  >
                    <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                      {cmd.icon || <CommandIcon className="w-3 h-3" />}
                    </span>
                    <span className="text-xs flex-1 truncate">{cmd.label}</span>
                    {cmd.keys && cmd.keys.length > 0 && (
                      <span className="flex items-center gap-1 flex-shrink-0">
                        {cmd.keys.map((c, i) => (
                          <kbd key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-muted/40 border border-border/40 text-muted-foreground/80 font-mono">
                            {formatCombo(c)}
                          </kbd>
                        ))}
                      </span>
                    )}
                    {isActive && <ArrowRight className="w-3 h-3 text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-3 px-3 py-1.5 border-t border-border/50 text-[9px] text-muted-foreground" data-testid="command-palette-footer">
          <Hint k="↑↓">navigate</Hint>
          <Hint k="↵">run</Hint>
          <Hint k="esc">close</Hint>
          <span className="ml-auto opacity-60">{flatList.length} command{flatList.length !== 1 ? 's' : ''}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Hint({ k, children }) {
  return (
    <span className="flex items-center gap-1">
      <kbd className="px-1 py-0.5 rounded bg-muted/40 border border-border/40 font-mono">{k}</kbd>
      {children}
    </span>
  );
}

/** Static + role-aware navigation commands. */
function buildNavCommands({ user, navigate, hasPermission }) {
  const nav = (path) => () => navigate(path);
  const cmds = [
    { id: 'nav.home',          label: `Go to ${getTerm('home')}`, category: 'Navigation', icon: <Sparkles className="w-3 h-3" />, run: nav('/dashboard') },
    { id: 'nav.quickstart',    label: 'Quickstart (AI dashboard generation)', category: 'Navigation', icon: <Sparkles className="w-3 h-3" />, run: nav('/quickstart') },
    { id: 'nav.insights',      label: `Go to ${getTerm('insights')}`, category: 'Navigation', icon: <BarChart3 className="w-3 h-3" />, run: nav('/dashboards') },
    { id: 'nav.report-new',    label: 'New Report', category: 'Navigation', icon: <BarChart3 className="w-3 h-3" />, run: nav('/reports/builder?new=1') },
    { id: 'nav.ai-insights',   label: `Go to ${getTerm('ai_insights')}`, category: 'Navigation', icon: <Sparkles className="w-3 h-3" />, run: nav('/ai-insights') },
    { id: 'nav.visual-library', label: getTerm('visual_library'), category: 'Navigation', icon: <Layers className="w-3 h-3" />, run: nav('/visual-library') },
    { id: 'nav.data-sources',  label: `Go to ${getTerm('sources')}`, category: 'Navigation', icon: <Database className="w-3 h-3" />, run: nav('/data-sources') },
    { id: 'nav.semantic-models', label: 'Go to Semantic Models', category: 'Navigation', icon: <Folder className="w-3 h-3" />, run: nav('/semantic-models') },
    { id: 'nav.data-prep',     label: 'Open Data Prep', category: 'Navigation', icon: <Database className="w-3 h-3" />, run: nav('/data-prep') },
    { id: 'nav.scheduling',    label: 'Open Scheduling', category: 'Navigation', icon: <Settings className="w-3 h-3" />, run: nav('/scheduling') },
    { id: 'nav.alerts',        label: 'Go to Alerts', category: 'Navigation', icon: <Bell className="w-3 h-3" />, run: nav('/alerts') },
    { id: 'nav.anomaly-scans', label: 'Anomaly Detection', category: 'Navigation', icon: <Sparkles className="w-3 h-3" />, run: nav('/anomaly-scans') },
    { id: 'nav.ai-copilot',    label: `Open ${getTerm('ai_explore')}`, category: 'Navigation', icon: <Sparkles className="w-3 h-3" />, run: nav('/ai-copilot') },
    { id: 'nav.settings',      label: 'Open Settings', category: 'Navigation', icon: <Settings className="w-3 h-3" />, run: nav('/settings') },
  ];
  // Administration hub + capability-visible admin pages (from the shared config).
  cmds.push({ id: 'nav.administration', label: 'Go to Administration', category: 'Administration', icon: <Settings className="w-3 h-3" />, run: nav('/administration') });
  try {
    visibleAdminCategories(user, hasPermission, 'workspace_admin').forEach((cat) => {
      cmds.push({ id: `admin.cat.${cat.id}`, label: `${cat.label} (Administration)`, category: 'Administration', icon: <Settings className="w-3 h-3" />, run: nav(`/administration/${cat.id}`) });
      cat.items.filter((it) => !it.future).forEach((it) => {
        cmds.push({ id: `admin.item.${cat.id}.${it.label}`, label: `${it.label} · ${cat.label}`, category: 'Administration', icon: <Settings className="w-3 h-3" />, run: nav(it.to) });
      });
    });
    const platformCats = visibleAdminCategories(user, hasPermission, 'platform_console');
    if (platformCats.length) {
      cmds.push({ id: 'nav.platform-console', label: 'Go to Platform Console', category: 'Platform Console', icon: <Settings className="w-3 h-3" />, run: nav('/platform-console') });
      platformCats.forEach((cat) => {
        cmds.push({ id: `plat.cat.${cat.id}`, label: `${cat.label} (Platform Console)`, category: 'Platform Console', icon: <Settings className="w-3 h-3" />, run: nav(`/platform-console/${cat.id}`) });
        cat.items.filter((it) => !it.future).forEach((it) => {
          cmds.push({ id: `plat.item.${cat.id}.${it.label}`, label: `${it.label} · ${cat.label}`, category: 'Platform Console', icon: <Settings className="w-3 h-3" />, run: nav(it.to) });
        });
      });
    }
  } catch { /* config load guard */ }
  return cmds;
}

function pickIconForCategory(cat) {
  switch ((cat || '').toLowerCase()) {
    case 'editing':    return <CommandIcon className="w-3 h-3" />;
    case 'navigation': return <ArrowRight className="w-3 h-3" />;
    case 'admin':      return <Settings className="w-3 h-3" />;
    default:           return <CommandIcon className="w-3 h-3" />;
  }
}

/**
 * Lazy-loads the user's actual content (reports, semantic models, alerts) and
 * surfaces each as a runnable "Open …" command. Cached for the page session
 * after first palette open. Returns [] until first open to avoid hitting the
 * API on every page render.
 */
function useContentCommands({ enabled, navigate }) {
  const [reports, setReports] = useState([]);
  const [models, setModels] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    fetchedRef.current = true;
    // Fetch all three in parallel; ignore failures so a single 403 doesn't break the palette.
    Promise.allSettled([
      api.getReports(),
      api.getSemanticModels(),
      api.getAlerts ? api.getAlerts() : Promise.resolve([]),
    ]).then(([r, m, a]) => {
      if (r.status === 'fulfilled') setReports(Array.isArray(r.value) ? r.value : []);
      if (m.status === 'fulfilled') setModels(Array.isArray(m.value) ? m.value : []);
      if (a.status === 'fulfilled') setAlerts(Array.isArray(a.value) ? a.value : []);
    });
  }, [enabled]);

  return useMemo(() => {
    const nav = (path) => () => navigate(path);
    const cmds = [];
    for (const r of reports) {
      const id = r.report_id || r.id;
      const name = r.name || 'Untitled report';
      if (!id) continue;
      cmds.push({
        id: `content.report.${id}`,
        label: name,
        category: 'Reports',
        keys: [],
        icon: <BarChart3 className="w-3 h-3" />,
        run: nav(`/reports/builder?id=${encodeURIComponent(id)}`),
      });
    }
    for (const m of models) {
      const id = m.model_id || m.id;
      const name = m.name || 'Untitled model';
      if (!id) continue;
      cmds.push({
        id: `content.model.${id}`,
        label: name,
        category: 'Semantic Models',
        keys: [],
        icon: <Folder className="w-3 h-3" />,
        run: nav(`/semantic-models?id=${encodeURIComponent(id)}`),
      });
    }
    for (const a of alerts) {
      const id = a.alert_id || a.id;
      const name = a.name || a.title || 'Untitled alert';
      if (!id) continue;
      cmds.push({
        id: `content.alert.${id}`,
        label: name,
        category: 'Alerts',
        keys: [],
        icon: <Bell className="w-3 h-3" />,
        run: nav(`/alerts?id=${encodeURIComponent(id)}`),
      });
    }
    return cmds;
  }, [reports, models, alerts, navigate]);
}

/**
 * Lightweight fuzzy filter — case-insensitive substring match against label
 * and category, with a small bonus for matches at word boundaries.
 */
function fuzzyFilter(commands, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return commands;
  const scored = [];
  for (const c of commands) {
    const label = (c.label || '').toLowerCase();
    const cat = (c.category || '').toLowerCase();
    const labelIdx = label.indexOf(q);
    const catIdx = cat.indexOf(q);
    if (labelIdx === -1 && catIdx === -1) continue;
    let score = 0;
    if (labelIdx === 0) score += 10;
    else if (labelIdx > 0) score += 5;
    if (label.split(/\s+/).some(w => w.startsWith(q))) score += 3;
    if (catIdx >= 0) score += 1;
    scored.push({ c, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.map(s => s.c);
}
